import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { BaseMap, DEFAULT_CENTER, Dot, FitBounds, MapClick, Pin } from "@/lib/mapProvider";

export const goldPin = {
  html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#d4af37;border:3px solid #0a0a0c;box-shadow:0 0 0 3px rgba(212,175,55,.35)"></div>`,
  size: [22, 22],
  anchor: [11, 22],
};

const R = 3958.8;
export const milesBetween = (a, b) => {
  const rad = (x) => (x * Math.PI) / 180;
  const dLat = rad(b[0] - a[0]);
  const dLng = rad(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const directionsUrl = (x) => `https://www.google.com/maps/dir/?api=1&destination=${x.lat},${x.lng}`;

// Self-contained so it also works inside the Google InfoWindow (separate React root)
export const UsedButton = ({ x, onLogged, light }) => {
  const [s, setS] = useState({ count: x.my_use_count || 0, done: x.used_today, thanks: false, busy: false });
  useEffect(() => { setS((v) => ({ ...v, count: x.my_use_count || 0, done: x.used_today || v.done })); }, [x.my_use_count, x.used_today]);
  const tap = async () => {
    setS((v) => ({ ...v, busy: true }));
    try {
      const { data } = await api.post(`/driver/hub/discounts/${x.id}/used`);
      setS({ count: data.my_use_count, done: true, thanks: true, busy: false });
      onLogged?.(x.id, data);
    } catch (e) { setS((v) => ({ ...v, busy: false })); toast.error(errMsg(e)); }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {s.done ? <span data-testid={`hub-discount-used-done-${x.id}`} className={light ? "font-semibold text-emerald-700" : "text-emerald-300"}>{s.thanks ? "Thanks — logged" : "Logged today"}</span>
        : <button data-testid={`hub-discount-used-${x.id}`} disabled={s.busy} onClick={tap}
          className={light ? "rounded-full bg-amber-500 px-2.5 py-1 font-semibold text-black" : "rounded-full border border-amber-400/50 px-3 py-1 text-amber-200 transition-colors duration-300 hover:bg-amber-400/10 disabled:opacity-50"}>I used this</button>}
      {s.count > 0 && <span data-testid={`hub-discount-used-count-${x.id}`} className={light ? "text-zinc-600" : "text-zinc-500"}>Used {s.count} time{s.count === 1 ? "" : "s"}</span>}
    </span>
  );
};

const PinPopup = ({ x, me, onLogged }) => {
  const dist = x.distance_miles ?? (me ? milesBetween(me, [x.lat, x.lng]) : null);
  return (
    <div data-testid={`hub-map-popup-${x.id}`} className="min-w-[160px] space-y-0.5 text-xs">
      <b className="block text-sm">{x.title}</b>
      <span className="block">{x.partner}</span>
      {x.code && <span className="block font-mono">Code: {x.code}</span>}
      {dist != null && <span data-testid={`hub-map-distance-${x.id}`} className="block">{dist.toFixed(1)} mi away</span>}
      <a data-testid={`hub-map-directions-${x.id}`} href={directionsUrl(x)} target="_blank" rel="noopener noreferrer" style={{ color: "#a16207", fontWeight: 600 }}>Directions</a>
      {onLogged && <div className="pt-1"><UsedButton x={x} onLogged={onLogged} light /></div>}
    </div>
  );
};

export const DiscountMap = ({ items, me, onLogged }) => {
  const located = items.filter((x) => x.lat != null && x.lng != null);
  const points = [...located.map((x) => [x.lat, x.lng]), ...(me ? [me] : [])];
  return (
    <div data-testid="hub-discount-map" className="overflow-hidden rounded-2xl border border-white/10">
      <BaseMap center={DEFAULT_CENTER} zoom={11} className="h-[260px] w-full sm:h-[340px]">
        {located.map((x) => <Pin key={x.id} position={[x.lat, x.lng]} icon={goldPin}><PinPopup x={x} me={me} onLogged={onLogged} /></Pin>)}
        {me && <Dot center={me} radius={8} fill="#38bdf8" stroke="#0a0a0c" strokeWeight={2} title="You are here" />}
        <FitBounds points={points} fitKey={`${points.length}-${me ? me.join(",") : ""}`} />
      </BaseMap>
    </div>
  );
};

export const PickOnMap = ({ value, onPick }) => (
  <div data-testid="hub-discount-pick-map" className="overflow-hidden rounded-xl border border-white/10">
    <BaseMap center={value || DEFAULT_CENTER} zoom={value ? 14 : 11} className="h-[240px] w-full">
      <MapClick onPick={(lat, lng) => onPick([+lat.toFixed(6), +lng.toFixed(6)])} />
      {value && <Pin position={value} icon={goldPin} />}
    </BaseMap>
  </div>
);
